import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActionSheetController, IonInput, MenuController, Platform } from "@ionic/angular";
import { AppRoutes } from "src/app/constants";
import { ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import { RootState } from "src/app/store";
import * as UserActions from "src/app/store/user/actions";
import * as FromUser from "src/app/store/user/selectors";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { Browser } from "@capacitor/browser";
import { EnvironmentService } from "src/app/services/environment/environment.service";
import { first, map } from "rxjs/operators";

@Component({
  selector: "app-login",
  templateUrl: "./login.page.html",
  styleUrls: ["./login.page.scss"],
})
export class LoginPage implements OnInit, OnDestroy {
  @ViewChild("emailInput") public emailInput: IonInput;
  @ViewChild("passwordInput") public passwordInput: IonInput;

  public showPassword: boolean;
  public showRegisterLink: boolean;
  public loading$: Observable<boolean> = this.store.select(FromUser.selectLoading);
  public routes: typeof AppRoutes = AppRoutes;
  public email = this.activatedRoute.snapshot.queryParamMap.get("email");
  public form: FormGroup;

  private environment$: BehaviorSubject<Environment> = this.environmentService.environment();
  private $usernameChanges: Subscription;

  constructor(
    private menu: MenuController,
    private actionSheetController: ActionSheetController,
    private store: Store<RootState>,
    private activatedRoute: ActivatedRoute,
    private environmentService: EnvironmentService,
    private platform: Platform,
  ) {
    this.menu.enable(false);
  }

  public ngOnInit(): void {
    this.form = new FormGroup({
      username: new FormControl(this.email, [Validators.required, Validators.email]),
      password: new FormControl(null, [Validators.required, Validators.minLength(2)]),
    });
    this.showRegisterLink = !this.platform.is("capacitor") || !this.platform.is("ios");

    this.$usernameChanges = this.form
      .get("username")
      .valueChanges.pipe(map(value => value?.trim()))
      .subscribe(username => this.form.patchValue({ username }, { emitEvent: false }));
  }

  public ngOnDestroy(): void {
    this.$usernameChanges?.unsubscribe();
  }

  public toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  public doLogin(): void {
    this.store.dispatch(UserActions.login({ login: this.form.value }));
  }

  public openHelp(): void {
    Browser.open({ url: "https://hilfe.wowfamily.de" });
  }

  public async handleFormAutocomplete(): Promise<void> {
    const emailInputEl = await this.emailInput.getInputElement();
    const passwordInputEl = await this.passwordInput.getInputElement();

    const username = emailInputEl.value;
    const password = passwordInputEl.value;

    this.form.patchValue({ email: username, password });
  }

  public async changeEnvironment(): Promise<void> {
    this.environment$.pipe(first()).subscribe(async environment => {
      const actionSheet = await this.actionSheetController.create({
        header: "Switch environment",
        buttons: [
          ...this.environmentService.environments.map((envName: EnvironmentName) => ({
            text: envName,
            role: environment.name === envName ? "selected" : undefined,
            handler: (): void => this.environmentService.changeEnvironment(envName),
          })),
          {
            text: "Dismiss",
            role: "cancel",
          },
        ],
      });

      await actionSheet.present();
    });
  }
}
